import type { RouteRecordRaw } from "vue-router"
import {
  buildApplyResponseRouteLocation,
  buildApplyStageRoutePath,
} from "@versatile-credit-apply/composition"
import { StepperId } from "@/models/stepper/stepper-id"
import {
  useProviderStore,
  useRouteConditionEvaluator,
} from "@versatile-credit-apply/sdk"
import WorkflowRoutes from "@/views/apply/workflows/workflow-routes"

// Already imported statically by the error routes, no need to dynamic import
// it again.
import GeneralErrorView from "@/views/error/GeneralErrorView.vue"
import {
  ApplyResponseCode,
  ProviderKey,
} from "@versatile-credit-apply/omni-apply-client"

const routes: RouteRecordRaw[] = [
  {
    name: "merchant.apply.intro",
    path: "/:experienceType/apply/intro",
    component: () => import("@/views/apply/MerchantApplyIntroView.vue"),
    meta: {
      layout: "merchantIntro",
      step: "applicant",
    },
  },
  {
    name: "apply.intro",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/intro",
    redirect: (to) => {
      return buildApplyStageRoutePath(
        to.params.providerKey as string,
        "/workflows/pre-provider",
        { params: to.params }
      )
    },
  },
  {
    name: "apply.workflows",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/workflows",
    children: [...WorkflowRoutes],
  },
  {
    name: "apply.form.application.initial",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/forms/application",
    meta: {
      step: "applicant",
    },
    redirect: (to) =>
      buildApplyStageRoutePath(
        to.params.providerKey as string,
        "/forms/application/primary/1",
        { params: to.params }
      ),
  },
  {
    name: "apply.form.application.initial.applicantType",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/forms/application/:applicantType",
    redirect: (to) =>
      buildApplyStageRoutePath(
        to.params.providerKey as string,
        `/forms/application/${to.params.applicantType}/1`,
        { params: to.params }
      ),
  },
  {
    name: "apply.forms.application",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/forms/application/:applicantType/:step",
    component: () => import("@/views/apply/ApplicationFormView.vue"),
    props: (route) => ({
      applicantType: route.params.applicantType,
      step: Number(route.params.step ?? "1"),
    }),
    meta: {
      step: "applicant",
      stepper: StepperId.APPLY,
      showPreviousDecision: true,
    },
  },
  {
    name: "apply.form.application.done",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/forms/application/done",
    redirect: (to) =>
      buildApplyStageRoutePath(to.params.providerKey as string, "/terms", {
        params: to.params,
      }),
  },
  {
    name: "apply.plan.launcher",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/plan-launcher",
    component: () => import("@/views/apply/PlanLauncherView.vue"),
    meta: {
      preventBack: true,
      step: "decision",
    },
  },
  {
    name: "apply.explore-offers",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/explore",
    component: () => import("@/views/apply/ExploreOffersView.vue"),
    meta: {
      preventBack: true,
      step: "decision",
    },
  },
  {
    name: "apply.review-offers",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/:planCode/review",
    component: () => import("@/views/provider/ProviderOverrideMeView.vue"),
    meta: {
      preventBack: true,
      step: "decision",
    },
  },
  {
    name: "apply.offer.accept",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/offer/accept",
    component: () => import("@/views/apply/AcceptOfferView.vue"),
    meta: {
      preventBack: true,
      step: "decision",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.responses.approved",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/responses/approved",
    component: () => import("@/views/provider/ProviderOverrideMeView.vue"),
    meta: {
      preventBack: true,
      step: "decision",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.responses.error",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/responses/error",
    component: GeneralErrorView,
    meta: {
      preventBack: true,
    },
  },
  {
    name: "apply.responses.declined",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/responses/declined",
    component: () => import("@/views/apply/DeclinedView.vue"),
    meta: {
      preventBack: true,
      step: "decision",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.responses.nooffer",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/responses/nooffer",
    component: () => import("@/views/apply/NoOfferView.vue"),
    meta: {
      preventBack: true,
      showPreviousDecision: true,
      step: "decision",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.responses.pending_full",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/responses/pending_full",
    component: () => import("@/views/apply/PendingFullView.vue"),
    beforeEnter: (to) => {
      const providerKey = decodeURIComponent(
        to.params.providerKey as string
      ) as ProviderKey

      if (providerKey) {
        const store = useProviderStore()
        const decision = store.findApplyResponse(providerKey)

        if (decision?.responseCode == ApplyResponseCode.PENDING) {
          const reasons = decision.pendingReasons ?? []

          if (reasons.length > 0) {
            const reason = reasons[0]

            return {
              path: to.fullPath.replace("_full", `/reason/${reason}`),
              replace: true,
            }
          }
        }
      }

      return true
    },
    meta: {
      preventBack: true,
      step: "decision",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.responses.pending_with_reason",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/responses/pending/reason/:reason",
    component: () => import("@/views/apply/PendingFullView.vue"),
    meta: {
      preventBack: true,
      step: "decision",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.responses.pending_offer",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/responses/pending_offer",
    component: () => import("@/views/apply/PendingOfferView.vue"),
    meta: {
      preventBack: true,
      showPreviousDecision: true,
      step: "decision",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.responses.preapproved",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/responses/preapproved",
    component: () => import("@/views/provider/ProviderOverrideMeView.vue"),
    meta: {
      preventBack: true,
      step: "decision",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.responses.prequalified",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/responses/prequalified",
    component: () => import("@/views/provider/ProviderOverrideMeView.vue"),
    meta: {
      preventBack: () => {
        const evaluator = useRouteConditionEvaluator()
        return evaluator("singleProviderOffer")
      },
      showPreviousDecision: "singleProviderOffer",
      step: "decision",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.patch",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/patch",
    component: () => import("@/views/apply/PatchSubmitView.vue"),
    meta: {
      preventBack: true,
      step: "decision",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.patch.success",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/patchSuccess",
    component: () => import("@/views/provider/ProviderOverrideMeView.vue"),
    meta: {
      preventBack: true,
      step: "terms",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.resume.start",
    path: "/providers/:providerKey/apply/resume",
    component: () => import("@/views/apply/StartResumeView.vue"),
  },
  {
    name: "apply.resume",
    path: "/providers/:providerKey/:experienceType/apply/resume",
    component: () => import("@/views/apply/ResumeView.vue"),
  },
  {
    name: "apply.resume.responses",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/resume/responses/:responseCode",
    redirect: (to) => {
      return buildApplyResponseRouteLocation({
        providerKey: to.params.providerKey as string,
        responseCode: to.params.responseCode as string,
      })
    },
    meta: {
      preventBack: true,
      step: "decision",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.start",
    path: "/providers/:providerKey/apply/start",
    component: () => import("@/views/apply/StartApplyView.vue"),
  },
  {
    name: "apply.start.from-resume",
    path: "/providers/:providerKey/:experienceType/apply/start",
    component: () => import("@/views/apply/StartApplyView.vue"),
  },
  {
    name: "apply.submit",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/submit",
    component: () => import("@/views/apply/ApplySubmitView.vue"),
    meta: {
      preventBack: true,
      step: "decision",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.full.terms",
    path: "/providers/:providerKey/:experienceType/apply/full/terms",
    component: () => import("@/views/provider/ProviderOverrideMeView.vue"),
    meta: {
      step: "terms",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.prequalify.terms",
    path: "/providers/:providerKey/:experienceType/apply/prequalify/terms",
    component: () => import("@/views/apply/PrequalifyTermsView.vue"),
    meta: {
      step: "terms",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.prescreen.terms",
    path: "/providers/:providerKey/:experienceType/apply/prescreen/terms",
    component: () => import("@/views/provider/ProviderOverrideMeView.vue"),
    meta: {
      step: "terms",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.offer.terms",
    path: "/providers/:providerKey/:experienceType/apply/offer/terms",
    component: () => import("@/views/provider/ProviderOverrideMeView.vue"),
    meta: {
      step: "terms",
      stepper: StepperId.APPLY,
    },
  },
  {
    name: "apply.complete",
    path: "/providers/:providerKey/:experienceType/apply/:applyStage/complete",
    component: () => import("@/views/apply/CompleteApplicationSubmitView.vue"),
    meta: {
      step: "decision",
      stepper: StepperId.APPLY,
    },
  },
]

export default routes
